class MegaMenuNav extends HTMLElement {
  constructor() {
    super();
    this.mq = window.matchMedia('(min-width: 1100px)');
    this.closeTimer = null;
    this.drawerRoot = null;
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onMqChange = this.onMqChange.bind(this);
    this.syncDrawer = this.syncDrawer.bind(this);
  }

  connectedCallback() {
    this.header = document.querySelector('.header-wrapper') || document.querySelector('sticky-header') || document.querySelector('header');
    this.bindDesktop();
    this.syncDrawer();
    document.addEventListener('keydown', this.onKeyDown);
    if (this.mq.addEventListener) {
      this.mq.addEventListener('change', this.onMqChange);
    } else {
      this.mq.addListener(this.onMqChange);
    }
    document.addEventListener('shopify:section:load', this.syncDrawer);
    document.addEventListener('shopify:block:select', this.syncDrawer);
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', this.syncDrawer, { once: true });
    }
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.onKeyDown);
    if (this.mq.removeEventListener) {
      this.mq.removeEventListener('change', this.onMqChange);
    } else {
      this.mq.removeListener(this.onMqChange);
    }
    document.removeEventListener('shopify:section:load', this.syncDrawer);
    document.removeEventListener('shopify:block:select', this.syncDrawer);
    document.removeEventListener('DOMContentLoaded', this.syncDrawer);
  }

  get items() {
    return this.querySelectorAll('.lb-mega-nav-item-wrap');
  }

  bindDesktop() {
    this.items.forEach((item) => this.bindItem(item, true));
  }

  bindItem(item, isDesktopSource) {
    const inner = item.querySelector(':scope > .lb-mega-nav-item');
    const trigger = inner?.querySelector(':scope > .lb-mega-nav-item__trigger');
    const panel = inner?.querySelector(':scope > .lb-mega-panel');
    if (!inner || !trigger) return;

    const openOn = inner.dataset.openOn || 'hover';

    trigger.addEventListener('click', (event) => {
      const desktop = this.mq.matches;
      if (!desktop || openOn === 'click' || trigger.getAttribute('href') === '#') {
        event.preventDefault();
        this.toggle(item);
      }
    });

    if (isDesktopSource) {
      item.addEventListener('mouseenter', () => {
        if (!this.mq.matches || openOn !== 'hover') return;
        this.clearCloseTimer();
        this.open(item);
      });
      item.addEventListener('mouseleave', () => {
        if (!this.mq.matches || openOn !== 'hover') return;
        this.scheduleClose(item);
      });
    }

    const parentLinks = item.querySelectorAll('.lb-mega-link-wrap:has(.lb-mega-link-wrap) > .lb-mega-link');
    parentLinks.forEach((link) => {
      link.addEventListener('click', (event) => {
        if (this.mq.matches) return;
        const children = link.parentElement?.querySelector(':scope > .lb-mega-link__children');
        if (!children || !children.children.length) return;
        event.preventDefault();
        link.parentElement.classList.toggle('is-open');
      });
    });

    this.bindColumnAccordions(item);
  }

  bindColumnAccordions(item) {
    const panelInner = item.querySelector('.lb-mega-panel__inner');
    if (!panelInner) return;
    const columns = panelInner.querySelectorAll('.lb-mega-column');
    if (columns.length < 2) return;

    columns.forEach((col) => {
      if (!col.classList.contains('lb-mega-column--has-heading')) return;
      const heading = col.querySelector(':scope > .lb-mega-column__heading');
      if (!heading || heading.dataset.lbBound === 'true') return;
      heading.dataset.lbBound = 'true';
      heading.addEventListener('click', (event) => {
        if (this.mq.matches) return;
        event.preventDefault();
        col.classList.toggle('is-open');
      });
    });
  }

  open(item) {
    this.items.forEach((other) => {
      if (other !== item) this.close(other);
    });
    if (this.drawerRoot) {
      this.drawerRoot.querySelectorAll('.lb-mega-nav-item-wrap.is-open').forEach((other) => {
        if (other !== item) this.close(other);
      });
    }
    this.setPanelTop();
    item.classList.add('is-open');
    const inner = item.querySelector(':scope > .lb-mega-nav-item');
    inner?.classList.add('is-open');
    const trigger = inner?.querySelector(':scope > .lb-mega-nav-item__trigger');
    trigger?.setAttribute('aria-expanded', 'true');
    if (this.header) this.header.preventHide = true;
  }

  close(item) {
    item.classList.remove('is-open');
    const inner = item.querySelector(':scope > .lb-mega-nav-item');
    inner?.classList.remove('is-open');
    const trigger = inner?.querySelector(':scope > .lb-mega-nav-item__trigger');
    trigger?.setAttribute('aria-expanded', 'false');
    const anyOpen = this.querySelector('.lb-mega-nav-item-wrap.is-open') || this.drawerRoot?.querySelector('.lb-mega-nav-item-wrap.is-open');
    if (!anyOpen && this.header) this.header.preventHide = false;
  }

  closeAll() {
    this.items.forEach((item) => this.close(item));
    this.drawerRoot?.querySelectorAll('.lb-mega-nav-item-wrap.is-open').forEach((item) => this.close(item));
    if (this.header) this.header.preventHide = false;
  }

  toggle(item) {
    if (item.classList.contains('is-open')) {
      this.close(item);
    } else {
      this.open(item);
    }
  }

  scheduleClose(item) {
    this.clearCloseTimer();
    this.closeTimer = setTimeout(() => this.close(item), 120);
  }

  clearCloseTimer() {
    if (this.closeTimer) {
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
  }

  setPanelTop() {
    if (!this.header) return;
    const bottom = Math.round(this.header.getBoundingClientRect().bottom);
    this.style.setProperty('--lb-mega-panel-top', `${bottom}px`);
    document.documentElement.style.setProperty('--lb-mega-panel-top', `${bottom}px`);
  }

  onKeyDown(event) {
    if (event.key === 'Escape') this.closeAll();
  }

  onMqChange() {
    this.closeAll();
    this.syncDrawer();
  }

  syncDrawer() {
    const target = document.querySelector('.menu-drawer__menu--theme-mega');
    if (!target) return;

    const source =
      this.querySelector('.list-menu--mega') ||
      document.querySelector('.header__inline-menu .list-menu--mega');
    const nativeMenu = target.parentElement?.querySelector('.menu-drawer__menu--native') ||
      document.querySelector('.menu-drawer__menu--native');

    this.drawerRoot = target;
    target.innerHTML = source?.innerHTML || '';
    target.querySelectorAll('[data-lb-bound]').forEach((el) => {
      delete el.dataset.lbBound;
    });
    target.querySelectorAll('.lb-mega-nav-item-wrap').forEach((item) => this.bindItem(item, false));

    const hasMegaItems = target.querySelector('.lb-mega-nav-item-wrap');
    const hasNativeItems = Boolean(nativeMenu?.children.length);

    if (hasMegaItems) {
      target.removeAttribute('hidden');
      nativeMenu?.setAttribute('hidden', '');
    } else if (hasNativeItems) {
      target.setAttribute('hidden', '');
      nativeMenu?.removeAttribute('hidden');
    } else {
      target.removeAttribute('hidden');
      nativeMenu?.setAttribute('hidden', '');
    }
  }
}

if (!customElements.get('mega-menu-nav')) {
  customElements.define('mega-menu-nav', MegaMenuNav);
}
